import { defineStore } from 'pinia'
import { io } from 'socket.io-client'
import { useAdminAuthStore } from './adminAuth'
import { useAdminStateStore } from './adminState'

function buildToken() {
  const t = localStorage.getItem('admin_token') || ''
  return t
}

export const useAdminSocketStore = defineStore('adminSocket', {
  state: () => ({
    socket: null,
    connected: false,
    connecting: false,
    lastEventAt: 0,
    eventLog: [],
  }),
  actions: {
    connect() {
      if (this.socket) return
      const auth = useAdminAuthStore()
      if (!auth.authenticated) return
      this.connecting = true
      const token = buildToken()
      const s = io('/admin', {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        auth: token ? { token } : undefined,
      })
      this.socket = s
      s.on('connect', () => {
        this.connected = true
        this.connecting = false
        this.pushLog('system', 'socket connected')
      })
      s.on('disconnect', () => {
        this.connected = false
        this.connecting = false
        this.pushLog('system', 'socket disconnected')
      })
      s.on('connect_error', (e) => {
        this.connected = false
        this.connecting = false
        this.pushLog('error', `socket connect_error: ${e?.message || String(e)}`)
      })
      s.on('admin:snapshot', (payload) => {
        this.pushLog('snapshot', 'received snapshot')
        const store = useAdminStateStore()
        if (payload?.workers) store.setWorkers(payload.workers)
        if (payload?.bots) store.setBots(payload.bots)
        if (payload?.panic) store.setPanicSwitches(payload.panic)
      })
      s.on('admin:workers', (payload) => {
        const store = useAdminStateStore()
        if (payload?.workers) store.setWorkers(payload.workers)
      })
      s.on('admin:bots', (payload) => {
        const store = useAdminStateStore()
        if (payload?.bots) store.setBots(payload.bots)
      })
      s.on('admin:panic', (payload) => {
        const store = useAdminStateStore()
        if (payload?.switches) store.setPanicSwitches(payload.switches)
      })
      s.on('admin:event:command_issued', (payload) => {
        this.pushLog('command', `issued ${payload.action} cmd=${payload.commandId}`)
        const store = useAdminStateStore()
        store.prependCommandEvent(payload)
      })
      s.on('admin:event:command_result', (payload) => {
        this.pushLog(
          'result',
          `result ${payload.commandId} ${payload.ok ? 'ok' : 'fail'} w=${payload.workerId}${payload.botId ? ' b=' + payload.botId : ''}`
        )
      })
      s.on('admin:socket_error', (payload) => {
        this.pushLog('error', payload?.error || 'socket error')
      })
    },
    disconnect() {
      if (!this.socket) return
      try {
        this.socket.disconnect(true)
      } catch {}
      this.socket = null
      this.connected = false
      this.connecting = false
    },
    emit(event, data, timeoutMs = 5000) {
      return new Promise((resolve, reject) => {
        if (!this.socket || !this.connected) {
          reject(new Error('socket_not_connected'))
          return
        }
        let cleared = false
        const timer = setTimeout(() => {
          if (cleared) return
          cleared = true
          reject(new Error('socket_timeout'))
        }, timeoutMs)
        this.socket.emit(event, data, (resp) => {
          if (cleared) return
          cleared = true
          clearTimeout(timer)
          resolve(resp)
        })
      })
    },
    pushLog(kind, message) {
      this.lastEventAt = Date.now()
      this.eventLog.unshift({ kind, message, at: this.lastEventAt })
      if (this.eventLog.length > 120) this.eventLog.pop()
    },
  },
})
